import React, { useState, useEffect } from 'react';
import { Search, Plus, Trash2, GripVertical, Check, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getProductImageSrc } from '../../../utils/product';

// Featured items structure: { id, order, isActive }

const FeaturedManager = ({ featuredProducts, onChange, allProducts, title, description }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);

    // Enrich the featured items with product details for display
    const [enrichedProducts, setEnrichedProducts] = useState([]);

    useEffect(() => {
        if (!featuredProducts || featuredProducts.length === 0) {
            setEnrichedProducts([]);
            return;
        }

        // Handle both legacy (array of strings) and new (array of objects) formats
        const normalizeItem = (item, index) => {
            if (typeof item === 'string') {
                return { id: item, order: index, isActive: true };
            }
            return item;
        };

        const enriched = featuredProducts
            .map((item, index) => {
                const normalized = normalizeItem(item, index);
                const product = allProducts.find(p => p.id === normalized.id);
                if (!product) return null;
                return { ...normalized, product };
            })
            .filter(Boolean)
            .sort((a, b) => a.order - b.order);

        setEnrichedProducts(enriched);
    }, [featuredProducts, allProducts]);

    const handleSearch = (val) => {
        setSearchTerm(val);
        // Note: Filtering logic moved to rendering or separate effect if heavy
    };

    // Filtered "All Products" list (excludes already selected ones if desired, or just shows status)
    const getFilteredProducts = () => {
        let list = allProducts;
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            list = list.filter(p => p.name && p.name.toLowerCase().includes(lower));
        }
        return list;
    };

    const displayProducts = getFilteredProducts();

    const addProduct = (product) => {
        // Check availability
        const exists = featuredProducts.some(item =>
            (typeof item === 'string' ? item : item.id) === product.id
        );

        if (exists) return;

        const newItem = {
            id: product.id,
            order: featuredProducts.length,
            isActive: true
        };

        onChange([...featuredProducts, newItem]);
    };

    const removeProduct = (id) => {
        const updated = featuredProducts.filter(item =>
            (typeof item === 'string' ? item : item.id) !== id
        );
        onChange(updated);
    };

    const toggleActive = (id) => {
        const updated = featuredProducts.map(item => {
            const itemId = typeof item === 'string' ? item : item.id;
            if (itemId !== id) return item;

            // Convert string to object if needed
            if (typeof item === 'string') {
                return { id, order: 0, isActive: false };
            }
            return { ...item, isActive: !item.isActive };
        });
        onChange(updated);
    };

    const moveUp = (index) => {
        if (index === 0) return;

        // Clone and normalize
        const newArr = featuredProducts.map((item, idx) =>
            typeof item === 'string' ? { id: item, order: idx, isActive: true } : item
        );

        // Swap
        [newArr[index - 1], newArr[index]] = [newArr[index], newArr[index - 1]];

        // Re-assign proper order
        newArr.forEach((item, idx) => item.order = idx);

        onChange(newArr);
    };

    const moveDown = (index) => {
        if (index === featuredProducts.length - 1) return;

        // Clone and normalize
        const newArr = featuredProducts.map((item, idx) =>
            typeof item === 'string' ? { id: item, order: idx, isActive: true } : item
        );

        // Swap
        [newArr[index + 1], newArr[index]] = [newArr[index], newArr[index + 1]];

        // Re-assign proper order
        newArr.forEach((item, idx) => item.order = idx);

        onChange(newArr);
    };

    const isSelected = (productId) => {
        return featuredProducts.some(item =>
            (typeof item === 'string' ? item : item.id) === productId
        );
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[600px]">
            {/* Left Column: All Products */}
            <div className="bg-white p-6 rounded-xl border border-gray-100 flex flex-col h-full shadow-sm">
                <h3 className="text-lg font-bold text-gray-800 mb-4">{t('admin.featured.allProducts', 'Tüm Ürünler')}</h3>

                <div className="relative mb-4">
                    <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder={t('admin.featured.searchPlaceholder', 'Ürün ismine göre ara...')}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </div>

                <div className="flex-1 overflow-y-auto pr-2 space-y-2">
                    {displayProducts.length === 0 ? (
                        <div className="text-center py-10 text-gray-400">
                            {t('admin.allProducts.noProducts', 'Ürün bulunamadı')}
                        </div>
                    ) : (
                        displayProducts.map(p => {
                            const selected = isSelected(p.id);
                            return (
                                <div
                                    key={p.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${selected ? 'bg-green-50 border-green-200 opacity-60' : 'hover:bg-gray-50 border-gray-100'}`}
                                >
                                    <img src={getProductImageSrc(p)} className="w-12 h-12 object-contain bg-white rounded border" alt="" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-gray-800 truncate">{p.name}</div>
                                        <div className="text-xs text-gray-500">{p.price} ₺</div>
                                    </div>

                                    {selected ? (
                                        <div className="flex items-center gap-1 text-green-700 text-xs font-bold px-3 py-1.5 bg-green-100 rounded-lg">
                                            <Check className="w-4 h-4" />
                                            <span className="hidden sm:inline">{t('common.added', 'Ekli')}</span>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => addProduct(p)}
                                            className="flex items-center gap-1 bg-white border border-gray-200 text-gray-600 hover:border-green-500 hover:text-green-600 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors"
                                        >
                                            <Plus className="w-4 h-4" />
                                            {t('common.addToFeatured', 'Ekle')}
                                        </button>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Right Column: Selected Featured */}
            <div className="bg-white p-6 rounded-xl border border-gray-100 flex flex-col h-full shadow-sm">
                <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        {title || t('admin.featured.selected', 'Seçilen Öne Çıkanlar')}
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                            {enrichedProducts.length}
                        </span>
                    </h3>
                    {description && (
                        <p className="text-sm text-gray-500 mt-1">{description}</p>
                    )}
                </div>

                {enrichedProducts.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed rounded-xl">
                        <Plus className="w-12 h-12 mb-2 opacity-20" />
                        <p>{t('admin.featured.emptyList', 'Liste boş')}</p>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                        {enrichedProducts.map((item, idx) => (
                            <div key={item.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${item.isActive ? 'bg-white border-gray-200 shadow-sm' : 'bg-gray-50 border-gray-200 opacity-75'}`}>
                                <div className="text-gray-400 cursor-grab active:cursor-grabbing">
                                    <GripVertical className="w-5 h-5" />
                                </div>
                                <div className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-xs font-bold text-gray-500">
                                    {idx + 1}
                                </div>
                                <img src={getProductImageSrc(item.product)} className="w-10 h-10 object-contain bg-white rounded border p-0.5" alt="" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-gray-800 truncate">{item.product?.name}</div>
                                    <div className="text-xs text-gray-500">{item.isActive ? t('common.active', 'Aktif') : t('common.inactive', 'Pasif')}</div>
                                </div>

                                <div className="flex items-center gap-1">
                                    <div className="flex flex-col gap-1 mr-2">
                                        <button
                                            onClick={() => moveUp(idx)}
                                            disabled={idx === 0}
                                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30 text-gray-500"
                                            title={t('common.moveUp')}
                                        >
                                            <ArrowUp className="w-3 h-3" />
                                        </button>
                                        <button
                                            onClick={() => moveDown(idx)}
                                            disabled={idx === enrichedProducts.length - 1}
                                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30 text-gray-500"
                                            title={t('common.moveDown')}
                                        >
                                            <ArrowDown className="w-3 h-3" />
                                        </button>
                                    </div>

                                    <button
                                        onClick={() => toggleActive(item.id)}
                                        className={`p-2 rounded-lg transition-colors ${item.isActive ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-gray-400 bg-gray-100 hover:bg-gray-200'}`}
                                        title={item.isActive ? t('common.makePassive', 'Pasif Yap') : t('common.makeActive', 'Aktif Yap')}
                                    >
                                        {item.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    </button>

                                    <button
                                        onClick={() => removeProduct(item.id)}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        title={t('common.remove')}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeaturedManager;
